/**
 * Admin Currency Management
 * Manage currencies and exchange rates
 */

import React, { useState, useEffect } from 'react';
import {
  DollarSign,
  Plus,
  Edit,
  Trash2,
  CheckCircle,
  RefreshCw,
  TrendingUp,
  Calendar,
} from 'lucide-react';

interface Currency {
  id: number;
  code: string;
  name: string;
  symbol: string;
  symbol_position: 'before' | 'after';
  decimal_places: number;
  is_enabled: number;
  is_base: number;
}

interface ExchangeRate {
  id: number;
  from_currency: string;
  to_currency: string;
  rate: number;
  effective_date: string;
}

export const Currencies: React.FC = () => {
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [rates, setRates] = useState<ExchangeRate[]>([]);
  const [baseCurrency, setBaseCurrency] = useState<Currency | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState<'currencies' | 'rates'>('currencies');

  useEffect(() => {
    loadCurrencies();
    loadRates();
  }, []);

  const loadCurrencies = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/admin/currencies?includeDisabled=true', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setCurrencies(data.data.currencies);
        const base = data.data.currencies.find((c: Currency) => c.is_base === 1);
        setBaseCurrency(base || null);
      }
    } catch (error) {
      console.error('Failed to load currencies:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRates = async () => {
    try {
      const response = await fetch('/api/v1/admin/currencies/rates/all', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setRates(data.data.rates);
      }
    } catch (error) {
      console.error('Failed to load exchange rates:', error);
    }
  };

  const handleSetBase = async (currencyId: number) => {
    if (!confirm('Set this currency as base? All prices will be stored in this currency.'))
      return;

    try {
      const response = await fetch(`/api/v1/admin/currencies/${currencyId}/set-base`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        loadCurrencies();
      }
    } catch (error) {
      console.error('Failed to set base currency:', error);
    }
  };

  const handleDeleteCurrency = async (currencyId: number) => {
    if (
      !confirm(
        'Are you sure you want to delete this currency? All associated exchange rates will be lost.'
      )
    )
      return;

    try {
      const response = await fetch(`/api/v1/admin/currencies/${currencyId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        loadCurrencies();
        loadRates();
      }
    } catch (error) {
      console.error('Failed to delete currency:', error);
    }
  };

  const handleFetchRates = async () => {
    try {
      const response = await fetch('/api/v1/admin/currencies/rates/fetch', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        if (data.data.count === 0) {
          alert('External rate fetching not configured. Please update rates manually.');
        } else {
          alert(`${data.data.count} exchange rates fetched successfully!`);
          loadRates();
        }
      }
    } catch (error) {
      console.error('Failed to fetch rates:', error);
    }
  };

  const formatAmount = (amount: number, currency: Currency): string => {
    const formatted = amount.toFixed(currency.decimal_places);
    return currency.symbol_position === 'before'
      ? `${currency.symbol}${formatted}`
      : `${formatted}${currency.symbol}`;
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <DollarSign className="w-6 h-6" />
              Currencies & Exchange Rates
            </h1>
            <p className="text-slate-400 mt-1">Manage multi-currency support</p>
          </div>
          <button className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center gap-2">
            <Plus className="w-4 h-4" />
            New Currency
          </button>
        </div>
      </div>

      {/* Base Currency Card */}
      {baseCurrency && (
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-blue-100 text-sm mb-1">Base Currency</div>
              <div className="text-white text-2xl font-bold flex items-center gap-3">
                <span className="text-3xl">{baseCurrency.symbol}</span>
                {baseCurrency.code} - {baseCurrency.name}
              </div>
              <div className="text-blue-100 text-sm mt-2">
                All prices are stored in this currency and converted for display
              </div>
            </div>
            <DollarSign className="w-16 h-16 text-blue-200 opacity-50" />
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-slate-700/50">
        <button
          onClick={() => setSelectedTab('currencies')}
          className={`px-4 py-2 font-medium transition-colors ${
            selectedTab === 'currencies'
              ? 'text-orange-400 border-b-2 border-blue-400'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          Currencies ({currencies.length})
        </button>
        <button
          onClick={() => setSelectedTab('rates')}
          className={`px-4 py-2 font-medium transition-colors ${
            selectedTab === 'rates'
              ? 'text-orange-400 border-b-2 border-blue-400'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          Exchange Rates ({rates.length})
        </button>
      </div>

      {/* Currencies Tab */}
      {selectedTab === 'currencies' && (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-700/50">
              <tr>
                <th className="text-left p-4 text-slate-300 font-medium">Currency</th>
                <th className="text-left p-4 text-slate-300 font-medium">Code</th>
                <th className="text-left p-4 text-slate-300 font-medium">Symbol</th>
                <th className="text-left p-4 text-slate-300 font-medium">Format</th>
                <th className="text-left p-4 text-slate-300 font-medium">Decimals</th>
                <th className="text-left p-4 text-slate-300 font-medium">Status</th>
                <th className="text-right p-4 text-slate-300 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-slate-400">
                    Loading...
                  </td>
                </tr>
              ) : currencies.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-slate-400">
                    No currencies found
                  </td>
                </tr>
              ) : (
                currencies.map((currency) => (
                  <tr key={currency.id} className="hover:bg-slate-700/30">
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium">{currency.name}</span>
                        {currency.is_base === 1 && (
                          <span className="px-2 py-0.5 bg-orange-900 text-blue-300 rounded text-xs">
                            BASE
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="text-slate-300 font-mono font-bold">
                        {currency.code}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className="text-slate-300 text-xl">{currency.symbol}</span>
                    </td>
                    <td className="p-4">
                      <span className="text-slate-400 text-sm">
                        {formatAmount(1234.56, currency)}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className="text-slate-300">{currency.decimal_places}</span>
                    </td>
                    <td className="p-4">
                      <span
                        className={`px-2 py-1 rounded text-sm ${
                          currency.is_enabled
                            ? 'bg-green-900 text-green-300'
                            : 'bg-slate-700/50 text-slate-400'
                        }`}
                      >
                        {currency.is_enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex justify-end gap-2">
                        {currency.is_base === 0 && (
                          <button
                            onClick={() => handleSetBase(currency.id)}
                            className="p-2 text-green-400 hover:bg-slate-700/50 rounded"
                            title="Set as Base"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          className="p-2 text-orange-400 hover:bg-slate-700/50 rounded"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        {currency.is_base === 0 && (
                          <button
                            onClick={() => handleDeleteCurrency(currency.id)}
                            className="p-2 text-red-400 hover:bg-slate-700/50 rounded"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Exchange Rates Tab */}
      {selectedTab === 'rates' && (
        <>
          {/* Rate Actions */}
          <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-4 mb-6">
            <div className="flex gap-4">
              <button
                onClick={handleFetchRates}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Fetch Latest Rates
              </button>
              <button className="px-4 py-2 bg-slate-700/50 text-white rounded-lg hover:bg-slate-600/50 flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Add Rate Manually
              </button>
            </div>
          </div>

          {/* Rates Table */}
          <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-700/50">
                <tr>
                  <th className="text-left p-4 text-slate-300 font-medium">From</th>
                  <th className="text-left p-4 text-slate-300 font-medium">To</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Rate</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Example</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Effective Date</th>
                  <th className="text-right p-4 text-slate-300 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {rates.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-slate-400">
                      No exchange rates found
                    </td>
                  </tr>
                ) : (
                  rates.map((rate) => {
                    const fromCurrency = currencies.find((c) => c.code === rate.from_currency);
                    const toCurrency = currencies.find((c) => c.code === rate.to_currency);

                    return (
                      <tr key={rate.id} className="hover:bg-slate-700/30">
                        <td className="p-4">
                          <span className="text-white font-mono font-bold">
                            {rate.from_currency}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className="text-white font-mono font-bold">
                            {rate.to_currency}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-green-400" />
                            <span className="text-white font-medium">
                              {rate.rate.toFixed(6)}
                            </span>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="text-slate-400 text-sm">
                            {fromCurrency?.symbol}100 = {toCurrency?.symbol}
                            {(100 * rate.rate).toFixed(2)}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-1 text-slate-400 text-sm">
                            <Calendar className="w-4 h-4" />
                            {new Date(rate.effective_date).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex justify-end gap-2">
                            <button
                              className="p-2 text-orange-400 hover:bg-slate-700/50 rounded"
                              title="Edit Rate"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              className="p-2 text-slate-400 hover:bg-slate-700/50 rounded"
                              title="View History"
                            >
                              <TrendingUp className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default Currencies;
